import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  FilterList,
  GetApp,
  Info,
  Refresh
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { auditLogsAPI } from '../services/api';
import { AuditLog, AuditLogFilters, AuditLogStats } from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { logger } from '../utils/logger';
import ResponsivePageHeader from '../components/Common/ResponsivePageHeader';

const AuditLogs: React.FC = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState<AuditLogStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<AuditLogFilters>({});
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [exporting, setExporting] = useState(false);

  // Load logs function
  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await auditLogsAPI.getLogs(
        page + 1,
        rowsPerPage,
        filters
      );

      // Ensure logs is always an array
      setLogs(response.logs || []);
      setTotalCount(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Greška pri učitavanju audit logova');
      // Ensure logs stays as empty array on error
      setLogs([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters, page, rowsPerPage]);

  // Load stats function
  const loadStats = useCallback(async () => {
    try {
      const statsResponse = await auditLogsAPI.getStats(
        undefined, // period
        filters.start_date,
        filters.end_date
      );
      // Ensure all properties exist with default values
      setStats({
        period_days: statsResponse?.period_days || 0,
        total_requests: statsResponse?.total_requests || 0,
        successful_requests: statsResponse?.successful_requests || 0,
        failed_requests: statsResponse?.failed_requests || 0,
        success_rate: statsResponse?.success_rate || 0,
        action_breakdown: statsResponse?.action_breakdown || {
          create: 0,
          read: 0,
          update: 0,
          delete: 0,
          auth: 0
        },
        resource_breakdown: statsResponse?.resource_breakdown || {},
        avg_response_time_ms: statsResponse?.avg_response_time_ms || 0,
        unique_users: statsResponse?.unique_users || 0,
        unique_ips: statsResponse?.unique_ips || 0,
        failed_auth_attempts: statsResponse?.failed_auth_attempts || 0
      });
    } catch (err: any) {
      logger.error('Failed to load audit stats:', err);
      setError('Nije moguće učitati statistiku revizije. Prikaz može biti nepotpun.');
      // Set default stats on error
      setStats({
        period_days: 0,
        total_requests: 0,
        successful_requests: 0,
        failed_requests: 0,
        success_rate: 0,
        action_breakdown: {
          create: 0,
          read: 0,
          update: 0,
          delete: 0,
          auth: 0
        },
        resource_breakdown: {},
        avg_response_time_ms: 0,
        unique_users: 0,
        unique_ips: 0,
        failed_auth_attempts: 0
      });
    }
  }, [filters.start_date, filters.end_date]);

  // Load logs on component mount and when filters change
  useEffect(() => {
    loadLogs();
    loadStats();
  }, [loadLogs, loadStats]);

  // Handle filter changes
  const handleFilterChange = (field: keyof AuditLogFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPage(0); // Reset to first page when filters change
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({});
    setPage(0);
  };

  // Apply filters (reload data)
  const applyFilters = () => {
    setPage(0);
    loadLogs();
    loadStats();
  };

  // Handle page change
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  // Export logs
  const handleExport = async (exportFormat: 'csv' | 'json') => {
    try {
      setExporting(true);
      const blob = await auditLogsAPI.exportLogs(exportFormat, filters);

      // Create download link
      const url = globalThis.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${exportFormat}-${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      globalThis.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.message || 'Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const getStatusChip = (success: boolean) => (
    <Chip
      label={success ? 'Success' : 'Error'}
      color={success ? 'success' : 'error'}
      size="small"
    />
  );

  const getMethodChip = (method: string) => (
    <Chip
      label={method}
      color={(() => {
        if (method === 'GET') return 'primary';
        if (method === 'POST') return 'success';
        if (method === 'PUT') return 'warning';
        return 'error';
      })()}
      size="small"
      variant="outlined"
    />
  );

  return (
    <Box p={3}>
      <ResponsivePageHeader
        title="Audit Logovi"
        subtitle="Praćenje svih aktivnosti i promena u sistemu"
        userInfo={{
          name: user?.email?.split('@')[0] || 'Korisnik',
          email: user?.email,
          company: user?.company_name || 'OmniPass Portal',
          role: user?.role
        }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Header Controls */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Sakrij filtere' : 'Prikaži filtere'}
          </Button>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadLogs}
            disabled={loading}
          >
            Osveži
          </Button>
        </Box>
        <Button
          variant="contained"
          startIcon={<GetApp />}
          onClick={() => handleExport('csv')}
          disabled={exporting}
        >
          Export
        </Button>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Box display="flex" flexWrap="wrap" gap={3} mb={3}>
          <Box flex="1 1 250px">
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Ukupno zahteva
                </Typography>
                <Typography variant="h5" component="div">
                  {(stats.total_requests || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1 1 250px">
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Procenat uspešnih
                </Typography>
                <Typography variant="h5" component="div" color="success.main">
                  {(stats.success_rate || 0).toFixed(1)}%
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1 1 250px">
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Prosečno vreme odgovora
                </Typography>
                <Typography variant="h5" component="div">
                  {(stats.avg_response_time_ms || 0).toFixed(0)}ms
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box flex="1 1 250px">
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Jedinstveni korisnici
                </Typography>
                <Typography variant="h5" component="div">
                  {(stats.unique_users || 0).toLocaleString()}
                </Typography>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Filters */}
      {showFilters && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Filteri
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <Box flex="1 1 200px">
              <TextField
                label="Početni datum"
                type="date"
                value={filters.start_date || ''}
                onChange={(e) => handleFilterChange('start_date', e.target.value || undefined)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="Krajnji datum"
                type="date"
                value={filters.end_date || ''}
                onChange={(e) => handleFilterChange('end_date', e.target.value || undefined)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="Akcija"
                select
                value={filters.action || ''}
                onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Sve</MenuItem>
                <MenuItem value="CREATE">Kreiranje</MenuItem>
                <MenuItem value="READ">Čitanje</MenuItem>
                <MenuItem value="UPDATE">Ažuriranje</MenuItem>
                <MenuItem value="DELETE">Brisanje</MenuItem>
                <MenuItem value="LOGIN">Prijavljivanje</MenuItem>
                <MenuItem value="LOGOUT">Odjavljivanje</MenuItem>
              </TextField>
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="HTTP metoda"
                select
                value={filters.method || ''}
                onChange={(e) => handleFilterChange('method', e.target.value || undefined)}
                fullWidth
                size="small"
              >
                <MenuItem value="">Sve</MenuItem>
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
              </TextField>
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="Email korisnika"
                value={filters.user_email || ''}
                onChange={(e) => handleFilterChange('user_email', e.target.value || undefined)}
                fullWidth
                size="small"
              />
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="Endpoint"
                value={filters.endpoint || ''}
                onChange={(e) => handleFilterChange('endpoint', e.target.value || undefined)}
                fullWidth
                size="small"
              />
            </Box>
            <Box flex="1 1 200px">
              <TextField
                label="Status"
                select
                value={filters.success?.toString() || ''}
                onChange={(e) => handleFilterChange('success', e.target.value === '' ? undefined : e.target.value === 'true')}
                fullWidth
                size="small"
              >
                <MenuItem value="">Sve</MenuItem>
                <MenuItem value="true">Uspešno</MenuItem>
                <MenuItem value="false">Greška</MenuItem>
              </TextField>
            </Box>
            <Box flex="1 1 200px">
              <Box display="flex" gap={1}>
                <Button onClick={clearFilters} variant="outlined" size="small">
                  Clear
                </Button>
                <Button onClick={applyFilters} variant="contained" size="small">
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Audit Logs Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Vreme</TableCell>
                <TableCell>Akcija</TableCell>
                <TableCell>Korisnik</TableCell>
                <TableCell>Endpoint</TableCell>
                <TableCell>Metoda</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Vreme odgovora</TableCell>
                <TableCell>IP adresa</TableCell>
                <TableCell>Akcije</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !logs || logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Nema pronađenih audit logova
                  </TableCell>
                </TableRow>
              ) : (
                  logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      {format(parseISO(log.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                    </TableCell>
                    <TableCell>{log.action}</TableCell>
                    <TableCell>
                      {log.user_email ? (
                        <>
                          {log.user_email}
                          {log.user_role && (
                            <Typography variant="caption" display="block" color="textSecondary">
                              {log.user_role}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="textSecondary" fontStyle="italic">
                          System
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{log.endpoint}</TableCell>
                    <TableCell>
                      {getMethodChip(log.method)}
                    </TableCell>
                    <TableCell>
                      {getStatusChip(log.success)}
                      <Typography variant="caption" display="block" color="textSecondary">
                        {log.response_status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {log.response_time_ms ? `${log.response_time_ms}ms` : 'N/A'}
                    </TableCell>
                    <TableCell>{log.ip_address}</TableCell>
                    <TableCell>
                      <Tooltip title="Prikaži detalje">
                        <IconButton size="small" onClick={() => setSelectedLog(log)}>
                          <Info />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Log Details Dialog */}
      <Dialog
        open={selectedLog !== null}
        onClose={() => setSelectedLog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalji audit loga</DialogTitle>
        <DialogContent>
          {selectedLog && (
            <Box>
              <Box display="flex" flexWrap="wrap" gap={2}>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Vreme:</Typography>
                  <Typography variant="body2">
                    {format(parseISO(selectedLog.timestamp), 'yyyy-MM-dd HH:mm:ss')}
                  </Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Akcija:</Typography>
                  <Typography variant="body2">{selectedLog.action}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Korisnik:</Typography>
                  <Typography variant="body2">
                    {selectedLog.user_email || 'Anoniman'} ({selectedLog.user_role || 'Nema ulogu'})
                  </Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">ID kompanije:</Typography>
                  <Typography variant="body2">{selectedLog.company_id}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Endpoint:</Typography>
                  <Typography variant="body2">{selectedLog.endpoint}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Metoda:</Typography>
                  <Typography variant="body2">{selectedLog.method}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Status odgovora:</Typography>
                  <Typography variant="body2">{selectedLog.response_status}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Uspešno:</Typography>
                  <Typography variant="body2">{selectedLog.success ? 'Da' : 'Ne'}</Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">Vreme odgovora:</Typography>
                  <Typography variant="body2">
                    {selectedLog.response_time_ms ? `${selectedLog.response_time_ms}ms` : 'N/A'}
                  </Typography>
                </Box>
                <Box flex="1 1 300px">
                  <Typography variant="subtitle2">IP adresa:</Typography>
                  <Typography variant="body2">{selectedLog.ip_address}</Typography>
                </Box>
                <Box width="100%">
                  <Typography variant="subtitle2">User Agent:</Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selectedLog.user_agent || 'N/A'}
                  </Typography>
                </Box>
                {selectedLog.request_data && (
                  <Box width="100%">
                    <Typography variant="subtitle2">Request Data:</Typography>
                    <Paper sx={{ p: 1, bgcolor: 'grey.100', mt: 1 }}>
                      <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                        {JSON.stringify(selectedLog.request_data, null, 2)}
                      </Typography>
                    </Paper>
                  </Box>
                )}
                {selectedLog.error_message && (
                  <Box width="100%">
                    <Typography variant="subtitle2">Error Message:</Typography>
                    <Typography variant="body2" color="error">
                      {selectedLog.error_message}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedLog(null)}>Zatvori</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditLogs;
